import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type {
  CreateCoverageList,
  CreateReportTemplate,
  TemplateDocument,
  UpdateCoverageList,
  UpdateReport,
  UpdateSection,
} from '@headline/schemas'
import { api } from './api'

/**
 * Report templates (CLI-643). Keyed under the account family so an account deletion's cache
 * clear takes them along. The list is summaries (names, timestamps); the document only rides
 * on the detail query, which is the editor's working copy.
 */
export function reportTemplatesQuery(accountId: string) {
  return queryOptions({
    queryKey: ['accounts', accountId, 'report-templates'],
    queryFn: async () => {
      const res = await api.api.accounts[':accountId']['report-templates'].$get({
        param: { accountId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load templates')
      }
      return res.json()
    },
  })
}

export const reportsOverviewQuery = queryOptions({
  queryKey: ['reports-overview'],
  queryFn: async () => {
    const res = await api.api.reports.overview.$get()
    if (!res.ok) throw new Error('Failed to load reports overview')
    return res.json()
  },
})

/** What deleting a report would destroy. Read only while the confirmation is open. */
export function reportDeletionScopeQuery(reportId: string) {
  return queryOptions({
    queryKey: ['report-deletion-scope', reportId],
    queryFn: async () => {
      const res = await api.api.reports[':id']['deletion-scope'].$get({ param: { id: reportId } })
      if (!res.ok) throw new Error('Could not read what this report holds.')
      return res.json()
    },
    // A count that is stale by a draft is a count that lies in a confirmation.
    staleTime: 0,
    gcTime: 0,
  })
}

export function reportQuery(reportId: string) {
  return queryOptions({
    queryKey: ['report', reportId],
    queryFn: async () => {
      const res = await api.api.reports[':id'].$get({ param: { id: reportId } })
      if (res.status === 404)
        throw new Error('This report no longer exists — it may have been deleted.')
      if (!res.ok) throw new Error('Failed to load report')
      return res.json()
    },
  })
}

export function coverageListsQuery(accountId: string) {
  return queryOptions({
    queryKey: ['coverage-lists', accountId],
    queryFn: async () => {
      const res = await api.api['coverage-lists'].$get({ query: { accountId } })
      if (!res.ok) throw new Error('Failed to load coverage lists')
      return res.json()
    },
  })
}

/**
 * The internal Coverage List Library — every list once, with member counts, in one request
 * (CLI-855). Keyed under the `coverage-lists` family so every list write that invalidates the
 * family refreshes it.
 */
export const coverageLibraryQuery = queryOptions({
  queryKey: ['coverage-lists', 'library'],
  queryFn: async () => {
    const res = await api.api['coverage-lists'].library.$get()
    if (!res.ok) throw new Error('Failed to load coverage lists')
    return res.json()
  },
})

/** One list with its members, for the library's edit dialog (the library rows carry counts only). */
export function coverageListQuery(listId: string) {
  return queryOptions({
    queryKey: ['coverage-lists', 'list', listId],
    queryFn: async () => {
      const res = await api.api['coverage-lists'][':id'].$get({ param: { id: listId } })
      if (!res.ok) throw new Error('Failed to load the list')
      return res.json()
    },
  })
}

export function useCreateReport(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { name: string }) => {
      const res = await api.api.reports.$post({ json: { subaccountId, name: input.name } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to create report')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports', subaccountId] }),
  })
}

export function useUpdateReport(reportId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (patch: UpdateReport) => {
      const res = await api.api.reports[':id'].$patch({ param: { id: reportId }, json: patch })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to save report')
      }
      return res.json()
    },
    onSuccess: (report) => {
      queryClient.setQueryData(reportQuery(reportId).queryKey, report)
      void queryClient.invalidateQueries({ queryKey: ['reports', report.subaccountId] })
    },
  })
}

/**
 * Who a report can be assigned to. Fetched only when a picker opens — it is one provider round
 * trip, so it has no business running on every table render. `staleTime` is generous because the
 * list of colleagues changes far more slowly than a shift does.
 */
export const assignableUsersQuery = queryOptions({
  queryKey: ['assignable-users'],
  staleTime: 10 * 60 * 1000,
  queryFn: async () => {
    const res = await api.api.reports['assignable-users'].$get()
    if (!res.ok) {
      const body = await res.json()
      throw new Error('error' in body ? body.error.message : 'Failed to load people')
    }
    return res.json()
  },
})

/** Replace a report's assigned associates. The delivery queue reads them, so it refreshes too. */
export function useSetReportOwners(reportId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (userIds: string[]) => {
      const res = await api.api.reports[':id'].owners.$put({
        param: { id: reportId },
        json: { userIds },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to save the assignment')
      }
      return res.json()
    },
    onSuccess: (report) => {
      queryClient.setQueryData(reportQuery(reportId).queryKey, report)
      void queryClient.invalidateQueries({ queryKey: ['reports-overview'] })
      void queryClient.invalidateQueries({ queryKey: ['reports', report.subaccountId] })
    },
  })
}

export function useDeleteReport(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (reportId: string) => {
      const res = await api.api.reports[':id'].$delete({ param: { id: reportId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to delete report')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports', subaccountId] }),
  })
}

export function useCreateSection(reportId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { name: string }) => {
      const res = await api.api.reports[':id'].sections.$post({
        param: { id: reportId },
        json: { name: input.name },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to add header')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['report', reportId] }),
  })
}

export function useUpdateSection(reportId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { sectionId: string; patch: UpdateSection }) => {
      const res = await api.api.reports.sections[':sectionId'].$patch({
        param: { sectionId: input.sectionId },
        json: input.patch,
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to save header')
      }
      return res.json()
    },
    onSuccess: (_section, input) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['report', reportId] }),
        // Attaching a topic records that reports use it (CLI-744) — the badges read it.
        input.patch.topics ? queryClient.invalidateQueries({ queryKey: ['topics'] }) : null,
      ]),
  })
}

export function useDeleteSection(reportId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (sectionId: string) => {
      const res = await api.api.reports.sections[':sectionId'].$delete({
        param: { sectionId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to remove header')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['report', reportId] }),
  })
}

export function useCreateCoverageList(accountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: CreateCoverageList) => {
      const res = await api.api['coverage-lists'].$post({ json: input })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to create list')
      }
      return res.json()
    },
    // The payload's accountId, not the hook's: the create dialog can now pick the account
    // inside the popup, so the mutation may land on a different account than the mount-time one.
    // A GLOBAL list (accountId null) surfaces under every account's query, so it invalidates the
    // whole family; an account list only touches its own.
    onSuccess: (_list, variables) => {
      void queryClient.invalidateQueries({
        queryKey:
          variables.accountId === null
            ? ['coverage-lists']
            : ['coverage-lists', variables.accountId],
      })
      void queryClient.invalidateQueries({ queryKey: coverageLibraryQuery.queryKey })
    },
  })
}

/** `accountId` null = the caller doesn't know the owning account (the library edits any list). */
export function useUpdateCoverageList(accountId: string | null, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { listId: string; patch: UpdateCoverageList }) => {
      const res = await api.api['coverage-lists'][':id'].$patch({
        param: { id: input.listId },
        json: input.patch,
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to save list')
      }
      return res.json()
    },
    onSuccess: (list) => {
      // A global list is in every account's query, so it refreshes the whole family (library and
      // the dialog's single-list read included); an account list touches its own, plus those two.
      if (list.accountId === null || accountId === null)
        void queryClient.invalidateQueries({ queryKey: ['coverage-lists'] })
      else {
        void queryClient.invalidateQueries({ queryKey: ['coverage-lists', accountId] })
        void queryClient.invalidateQueries({ queryKey: coverageLibraryQuery.queryKey })
        void queryClient.invalidateQueries({ queryKey: coverageListQuery(list.id).queryKey })
      }
      // Lists are LIVE references — a member edit changes every report that points at it.
      void queryClient.invalidateQueries({ queryKey: ['report'] })
    },
  })
}

export function useDeleteCoverageList(accountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (listId: string) => {
      const res = await api.api['coverage-lists'][':id'].$delete({ param: { id: listId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to delete list')
      }
      return res.json()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coverage-lists', accountId] })
      void queryClient.invalidateQueries({ queryKey: coverageLibraryQuery.queryKey })
    },
  })
}

/**
 * Copy a coverage list into one or more scopes (each an accountId, or `null` for the global library).
 * Each target gets its own independent copy. Invalidates the whole coverage-lists family so every
 * touched account's list refreshes.
 */
export function useCopyCoverageList(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { listId: string; targets: Array<string | null> }) => {
      const res = await api.api['coverage-lists'][':id'].copies.$post({
        param: { id: input.listId },
        json: { targets: input.targets },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to copy the list')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['coverage-lists'] }),
  })
}

export function reportTemplateQuery(accountId: string, templateId: string) {
  return queryOptions({
    queryKey: ['accounts', accountId, 'report-templates', templateId],
    queryFn: async () => {
      const res = await api.api.accounts[':accountId']['report-templates'][':templateId'].$get({
        param: { accountId, templateId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load the template')
      }
      return res.json()
    },
  })
}

export function useCreateReportTemplate(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string } & CreateReportTemplate) => {
      const { accountId, ...body } = input
      const res = await api.api.accounts[':accountId']['report-templates'].$post({
        param: { accountId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error(
          'error' in payload ? payload.error.message : 'Failed to create the template',
        )
      }
      return res.json()
    },
    onSuccess: (_created, input) =>
      queryClient.invalidateQueries({
        queryKey: ['accounts', input.accountId, 'report-templates'],
      }),
  })
}

export function useUpdateReportTemplate(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: {
      accountId: string
      templateId: string
      name?: string
      document?: TemplateDocument
    }) => {
      const { accountId, templateId, ...body } = input
      const res = await api.api.accounts[':accountId']['report-templates'][':templateId'].$put({
        param: { accountId, templateId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to save the template')
      }
      return res.json()
    },
    // Invalidating the list prefix reaches the detail key beneath it too.
    onSuccess: (_updated, input) =>
      queryClient.invalidateQueries({
        queryKey: ['accounts', input.accountId, 'report-templates'],
      }),
  })
}

export function useDeleteReportTemplate(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string; templateId: string }) => {
      const res = await api.api.accounts[':accountId']['report-templates'][':templateId'].$delete({
        param: input,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error(
          'error' in payload ? payload.error.message : 'Failed to delete the template',
        )
      }
      return res.json()
    },
    onSuccess: (_deleted, input) =>
      queryClient.invalidateQueries({
        queryKey: ['accounts', input.accountId, 'report-templates'],
      }),
  })
}
